import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography, Paper, Button, Divider, Alert, Snackbar } from "@mui/material";
import api from "../api/axios";
import {mobileReceiptTransaction,receiptMoney} from "../utils/mobileReceipt";
import jsPDF from "jspdf";
import { QRCodeCanvas } from "qrcode.react";
import DashboardLayout from "../components/Layout/DashboardLayout";
import { getUserFromStorage } from "../utils/auth";

export default function ReceiptPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile=location.pathname==='/mobile-payouts/receipt';
  const [mobileTx,setMobileTx]=useState(null),[receiptLoading,setReceiptLoading]=useState(isMobile),[receiptError,setReceiptError]=useState('');
  const tx = isMobile?mobileTx:location.state?.tx;

  const [user, setUser] = useState(null);
  const [notice, setNotice] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    const storedUser = getUserFromStorage();

    if (!storedUser || !["admin", "agent"].includes(storedUser.role)) {
      navigate("/login");
      return;
    }

    setUser(storedUser);
  }, [navigate]);

  useEffect(()=>{
    if(!user || !isMobile)return;
    const params=new URLSearchParams(location.search),reference=params.get('reference'),payment=params.get('payment');
    let active=true;const controller=new AbortController();setMobileTx(null);setReceiptLoading(true);setReceiptError('');
    if(!reference || !payment){setReceiptError('Référence ou paiement manquant.');setReceiptLoading(false);return;}
    api.get('/cash-payouts',{params:{reference},signal:controller.signal,timeout:15000})
      .then(({data})=>{if(active)setMobileTx(mobileReceiptTransaction(data,payment));})
      .catch(()=>{if(active && !controller.signal.aborted)setReceiptError('Impossible de charger ce reçu. Vérifiez le paiement enregistré et votre accès à cette agence.');})
      .finally(()=>{if(active)setReceiptLoading(false);});
    return()=>{active=false;controller.abort();};
  },[user,isMobile,location.search]);

  if (!user) return null;
  const returnBack=()=>isMobile?navigate(location.state?.returnTo || '/mobile-payouts'):navigate(-1);
  if(receiptLoading)return <Box p={2}><Typography>Chargement du reçu…</Typography></Box>;

  if (!tx) {
    return (
      <Box p={2}>
        <Typography color="error" variant="body2">
          {receiptError || "Aucune donnée de reçu disponible."}
        </Typography>
        <Button size="small" onClick={returnBack}>
          Retour
        </Button>
      </Box>
    );
  }

  const statusFrench = {
    pending: "En attente",
    partially_paid: "Partiellement payé",
    paid: "Retiré",
    cancelled: "Annulé",
  };

  const fmtDateFR = (date) => {
    if (!date) return "—";
    const raw=String(date).replace(' ','T');
    const instant=isMobile && !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw)?`${raw}Z`:date;
    return new Date(instant).toLocaleString("fr-FR", {
      ...(isMobile?{timeZone:"Africa/Kinshasa"}:{}),
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  const identifierLabel=isMobile?'RÉFÉRENCE MOBILE MONEY':'CODE SECRET';
  const footerCodeLabel=isMobile?'RÉFÉRENCE DU RETRAIT':'CODE À PRÉSENTER AU RETRAIT';
  const displayCurrency=isMobile?tx.currency:'$';
  const senderLabel=isMobile?'Opérateur':'Exp';
  const senderPhoneLabel=isMobile?'Numéro expéditeur':'Num Exp';
  const amountLabel=isMobile?'Montant remis':'Montant';
  const shownAmount=isMobile?receiptMoney(tx.payment_amount,tx.currency):tx.amount;
  const shownFee=isMobile?receiptMoney(tx.fee,tx.currency):tx.fee;
  const mobileRows=isMobile && tx.status==='partially_paid'?[
    ['Net total à recevoir',`${receiptMoney(tx.net_payable_amount,tx.currency)} ${tx.currency}`],
    ['Déjà retiré',`${receiptMoney(tx.paid_total,tx.currency)} ${tx.currency}`],
    ['Reste à retirer',`${receiptMoney(tx.remaining_amount,tx.currency)} ${tx.currency}`],
  ]:[];
  const statusLabel=isMobile?(tx.status==='paid'?'Retrait total':'Retrait partiel'):(statusFrench[tx.status] || tx.status);
  const eventDate = tx.last_payment_at || tx.last_status_update || tx.updated_at || null;
  const paidTotal = Number(tx.paid_total || 0);
  const remainingAmount = Number(
    tx.remaining_amount ?? Math.max(Number(tx.amount || 0) - paidTotal, 0)
  );

  const dateLabel = isMobile?"Date du paiement":
    tx.status === "paid"
      ? "Date du retrait"
      : tx.status === "partially_paid"
      ? "Date du dernier retrait partiel"
      : tx.status === "cancelled"
      ? "Date d'annulation"
      : "Date";

  const dateValue =
    tx.status === "pending" ? tx.created_at : eventDate || tx.created_at;

  const buildPdfBlob = () => {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: [80, isMobile?297:210] });
    const currency = tx.currency || "";
    const left = 5;
    const right = 75;
    let y = 7;

    const rule = () => {
      doc.setDrawColor(80);
      doc.setLineDashPattern([1, 1], 0);
      doc.line(left, y, right, y);
      doc.setLineDashPattern([], 0);
      y += 4;
    };
    const row = (label, value, options = {}) => {
      doc.setFont("helvetica", options.bold ? "bold" : "normal");
      doc.setFontSize(options.size || 8.5);
      const prefix = label ? `${label} : ` : "";
      const wrapped = doc.splitTextToSize(`${prefix}${value ?? "—"}`, 70);
      if(isMobile){wrapped.forEach(line=>{if(y>doc.internal.pageSize.getHeight()-10){doc.addPage();y=7;}doc.text(line,left,y);y+=3.7;});}
      else {doc.text(wrapped, left, y);y += wrapped.length * 3.7;}
    };
    const centered = (value, size = 9, bold = false) => {
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.setFontSize(size);
      doc.text(String(value), 40, y, { align: "center" });
      y += size * 0.42 + 2;
    };

    centered("REÇU DE TRANSACTION", 13, true);
    centered("Dieu est notre force", 9, true);
    y += 1;

    // QR code: reuse the same QR already rendered on the receipt.
    const qrCanvas = document.querySelector("#top-section canvas");
    if (qrCanvas) {
      const qrDataUrl = qrCanvas.toDataURL("image/png");
      doc.addImage(qrDataUrl, "PNG", 29, y, 22, 22);
      y += 25;
    }

    doc.setDrawColor(0);
    doc.rect(left, y, 70, 14);
    y += 4;
    centered(identifierLabel, 8, true);
    centered(tx.secret_code, isMobile?10:15, true);
    y += 1;

    rule();
    row(senderLabel, tx.sender_name);
    row(senderPhoneLabel, tx.sender_phone);
    row("Bénéf", tx.receiver_name);
    row("Num Bénéf", tx.receiver_phone);
    y += 1;
    rule();

    row(amountLabel, `${shownAmount} ${currency}`, { bold: true, size: 10 });
    if (tx.fee != null) row("Commission", `${shownFee} ${currency}`);
    rule();

    row("Statut", statusLabel, { bold: true });
    mobileRows.forEach(([label,value])=>row(label,value));
    if (!isMobile && tx.status === "partially_paid") {
      row("Déjà retiré", `${paidTotal.toFixed(2)} ${currency}`);
      row("Reste à retirer", `${remainingAmount.toFixed(2)} ${currency}`, { bold: true });
    }
    if(!isMobile){row("Provenance", `${tx.sender_country || "—"} / ${tx.sender_city || "—"}`);
    row("Destination", `${tx.receiver_country || "—"} / ${tx.receiver_city || "—"}`);}
    row(dateLabel, fmtDateFR(dateValue));

    if (tx.delegate_name) {
      rule();
      row("Délégué", tx.delegate_name);
      row("Téléphone", tx.delegate_phone || "—");
    }

    rule();
    // jsPDF's built-in Helvetica font does not contain Chinese glyphs.
    // Render only the Chinese address as a browser-canvas image so the PDF
    // keeps the exact Unicode text without shipping a large CJK font file.
    row("Adresse", "Chine :", { size: 7.5 });
    const chineseAddress = "广东省广州市越秀区越洋商贸城B区 3楼5317房号";
    const addressCanvas = document.createElement("canvas");
    const addressCtx = addressCanvas.getContext("2d");
    if (addressCtx) {
      const scale = 3;
      addressCtx.font = `${10 * scale}px "Microsoft YaHei", "PingFang SC", "Noto Sans CJK SC", sans-serif`;
      const maxWidthPx = 260 * scale;
      const chars = Array.from(chineseAddress);
      const addressLines = [];
      let currentLine = "";

      chars.forEach((char) => {
        const candidate = currentLine + char;
        if (addressCtx.measureText(candidate).width > maxWidthPx && currentLine) {
          addressLines.push(currentLine);
          currentLine = char;
        } else {
          currentLine = candidate;
        }
      });
      if (currentLine) addressLines.push(currentLine);

      addressCanvas.width = maxWidthPx;
      addressCanvas.height = Math.max(addressLines.length, 1) * 14 * scale;
      const drawCtx = addressCanvas.getContext("2d");
      drawCtx.fillStyle = "#ffffff";
      drawCtx.fillRect(0, 0, addressCanvas.width, addressCanvas.height);
      drawCtx.fillStyle = "#000000";
      drawCtx.font = `${10 * scale}px "Microsoft YaHei", "PingFang SC", "Noto Sans CJK SC", sans-serif`;
      drawCtx.textBaseline = "top";
      addressLines.forEach((line, index) => {
        drawCtx.fillText(line, 0, index * 14 * scale);
      });

      const imageHeightMm = Math.max(addressLines.length, 1) * 4.2;
      doc.addImage(addressCanvas.toDataURL("image/png"), "PNG", left, y, 70, imageHeightMm);
      y += imageHeightMm + 1;
    } else {
      row("", chineseAddress, { size: 7.5 });
    }
    row("", "Kinshasa : Croisement Av Itaga et Kasavubu,107 c/ Kinshasa, Galerie Grand Itaga, 1er niveau.", { size: 7.5 });
    row("Contact", "+86 13 242 307 259 / +243 85 339 22 04", { size: 7.5 });
    rule();
    centered("Vous servir est notre devoir", 9, true);
    y += 1;

    doc.setDrawColor(0);
    doc.rect(left, y, 70, 14);
    y += 4;
    centered(footerCodeLabel, 7.5, true);
    centered(tx.secret_code, isMobile?10:15, true);

    return doc.output("blob");
  };

  const downloadPdf = () => {
    const blob = buildPdfBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `recu-${tx.secret_code}${isMobile?`-${tx.payment_id}`:""}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setNotice({ open: true, message: "Reçu PDF téléchargé.", severity: "success" });
  };

  const shareReceipt = async () => {
    try {
      const blob = buildPdfBlob();
      const file = new File([blob], `recu-${tx.secret_code}${isMobile?`-${tx.payment_id}`:""}.pdf`, { type: "application/pdf" });
      if (navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }))) {
        await navigator.share({
          title: "Reçu de transaction",
          text: `Reçu de la transaction ${tx.secret_code}`,
          files: [file],
        });
        return;
      }
      setNotice({
        open: true,
        message: "Le partage direct n’est pas disponible sur cet appareil. Téléchargez le PDF puis partagez-le.",
        severity: "info",
      });
    } catch (err) {
      if (err?.name !== "AbortError") {
        setNotice({ open: true, message: "Impossible de partager le reçu.", severity: "error" });
      }
    }
  };

  const printTopSectionOnly = () => {
    const escape=value=>String(value ?? '—').replace(/[&<>"']/g,c=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
    const qrCanvas = document.querySelector("#top-section canvas");

    let qrImageHTML = "";

    if (qrCanvas) {
      const qrDataUrl = qrCanvas.toDataURL("image/png");

      qrImageHTML = `
        <div class="qr-wrap">
          <img src="${qrDataUrl}" width="85" height="85" />
        </div>
      `;
    }

    const rows = [
      `
      <div class="secret-box">
        <div class="secret-label">${identifierLabel}</div>
        <div class="secret-code">${escape(tx.secret_code)}</div>
      </div>
      `,

      `<div class="divider"></div>`,

      `<div class="line"><span class="label">${senderLabel} :</span> ${escape(tx.sender_name)}</div>`,
      `<div class="line"><span class="label">${senderPhoneLabel} :</span> ${escape(tx.sender_phone)}</div>`,

      `<div class="line"><span class="label">Bénéf :</span> ${escape(tx.receiver_name)}</div>`,
      `<div class="line"><span class="label">Num Bénéf :</span> ${escape(tx.receiver_phone)}</div>`,

      `<div class="divider"></div>`,

      `<div class="line amount-line"><span class="label">${amountLabel} :</span> ${escape(shownAmount)} ${escape(displayCurrency)}</div>`,

      tx.fee != null
        ? `<div class="line"><span class="label">Commission :</span> ${escape(shownFee)} ${escape(displayCurrency)}</div>`
        : "",

      `<div class="divider"></div>`,

      `<div class="line"><span class="label">Statut :</span> ${
        escape(statusLabel)
      }</div>`,

      !isMobile && tx.status === "partially_paid"
        ? `<div class="line"><span class="label">Déjà retiré :</span> ${paidTotal.toFixed(2)} ${tx.currency || ""}</div>`
        : "",

      !isMobile && tx.status === "partially_paid"
        ? `<div class="line amount-line"><span class="label">Reste à retirer :</span> ${remainingAmount.toFixed(2)} ${tx.currency || ""}</div>`
        : "",

      isMobile?"":`<div class="line"><span class="label">Provenance :</span> ${escape(tx.sender_country)} / ${escape(tx.sender_city)}</div>`,

      isMobile?"":`<div class="line"><span class="label">Destination :</span> ${escape(tx.receiver_country)} / ${escape(tx.receiver_city)}</div>`,

      `<div class="line"><span class="label">${dateLabel} :</span> ${fmtDateFR(
        dateValue
      )}</div>`,
    ];

    if(isMobile)rows.splice(10,0,...mobileRows.map(([label,value])=>`<div class="line"><span class="label">${escape(label)} :</span> ${escape(value)}</div>`));

    if (tx.delegate_name && tx.delegate_phone) {
      rows.push(`<div class="divider"></div>`);
      rows.push(
        `<div class="line"><span class="label">Délégué :</span> ${escape(tx.delegate_name)}</div>`
      );
      rows.push(
        `<div class="line"><span class="label">Téléphone :</span> ${escape(tx.delegate_phone)}</div>`
      );
    }

    rows.push(`<div class="divider"></div>`);

    rows.push(`
    <div class="line small-text">
    <span class="label">Adresse :</span><br/>
    Chine: 广东省广州市越秀区越洋商贸城B区 3楼5317房号
    </div>
    `);

    rows.push(`
    <div class="line small-text">
    Kinshasa : Croisement Av Itaga et Kasavubu,107 c/ Kinshasa,
    Galerie Grand Itaga, 1er niveau.
    </div>
    `);

    rows.push(`
    <div class="line small-text">
    <span class="label">Contact :</span>
    +86 13 242 307 259 / +243 85 339 22 04
    </div>
    `);

    rows.push(`<div class="divider"></div>`);

    rows.push(`
    <div class="footer-text">
    Vous servir est notre devoir
    </div>
    `);

    rows.push(`<div class="divider"></div>`);

    rows.push(`
    <div class="secret-box">
    <div class="secret-label">${footerCodeLabel}</div>
    <div class="secret-code">${escape(tx.secret_code)}</div>
    </div>
    `);

    const newWin = window.open("", "_blank");

    if(!newWin){setNotice({open:true,message:"Autorisez les fenêtres contextuelles pour imprimer le reçu.",severity:"error"});return;}
    newWin.opener=null;
    newWin.document.write(`
<html>
<head>

<title>Reçu</title>

<style>

@page {
  size: 80mm auto;
  margin: 3mm;
}

html, body{
margin:0;
padding:0;
width:100%;
}

body{
font-family: Arial, Helvetica, sans-serif;
font-size:11px;
line-height:1.25;
letter-spacing:0.2px;
}

.receipt{
width:72mm;
margin:0 auto;
}

.title{
text-align:center;
font-size:14px;
font-weight:800;
margin-bottom:2px;
}

.subtitle{
text-align:center;
font-size:11px;
font-weight:700;
margin-bottom:5px;
}

.qr-wrap{
text-align:center;
margin:4px 0 8px 0;
}

.divider{
border-top:1px dashed #000;
margin:6px 0;
}

.line{
margin:3px 0;
}

.label{
font-weight:700;
}

.secret-box{
border:1px solid #000;
padding:5px 4px;
text-align:center;
margin:6px 0;
}

.secret-label{
font-size:10px;
font-weight:700;
margin-bottom:2px;
}

.secret-code{
overflow-wrap:anywhere;
font-size:18px;
font-weight:900;
letter-spacing:1.5px;
}

.amount-line{
font-weight:700;
font-size:12px;
}

.footer-text{
text-align:center;
font-size:12px;
font-weight:800;
margin-top:5px;
}

.small-text{
font-size:10px;
}

</style>

</head>

<body>

<div class="receipt">

<div class="title">REÇU DE TRANSACTION</div>
<div class="subtitle">Dieu est notre force</div>

${qrImageHTML}

${rows.join("")}

</div>

</body>
</html>
`);

    newWin.document.close();

    setTimeout(() => {
      newWin.print();
      newWin.close();
    }, 400);
  };

  const storedUser2 = getUserFromStorage();

  return (
    <DashboardLayout role={storedUser2?.role}>
      <Box
        p={2}
        sx={{
          maxWidth: 350,
          margin: "0 auto",
          fontFamily: "Arial, sans-serif",
        }}
      >
        <Paper id="top-section" sx={{ p: 1.5, mb: 2, border: "1px solid #000" }}>
          <Box textAlign="center" mb={1}>
            <Typography fontSize={18} fontWeight="bold">
              REÇU DE TRANSACTION
            </Typography>

            <Typography fontSize={12} fontWeight="bold">
              Dieu est notre force
            </Typography>
          </Box>

          <Box textAlign="center" mb={1}>
            <QRCodeCanvas value={tx.qr_value || tx.secret_code} size={90} />
          </Box>

          <Box
            sx={{
              border: "1px solid #000",
              p: 0.5,
              textAlign: "center",
              mb: 1,
            }}
          >
            <Typography fontSize={11} fontWeight="bold">
              {identifierLabel}
            </Typography>

            <Typography fontSize={20} fontWeight="bold" letterSpacing={2}>
              {tx.secret_code}
            </Typography>
          </Box>

          <Divider sx={{ borderStyle: "dashed", my: 0.6 }} />

          <Typography fontSize={12}>
            <b>{senderLabel} :</b> {tx.sender_name}
          </Typography>

          <Typography fontSize={12}>
            <b>{senderPhoneLabel} :</b> {tx.sender_phone}
          </Typography>

          <Typography fontSize={12}>
            <b>Bénéf :</b> {tx.receiver_name}
          </Typography>

          <Typography fontSize={12}>
            <b>Num Bénéf :</b> {tx.receiver_phone}
          </Typography>

          <Divider sx={{ borderStyle: "dashed", my: 0.6 }} />

          <Typography fontSize={13} fontWeight="bold">
            <b>{amountLabel} :</b> {shownAmount} {displayCurrency}
          </Typography>

          {tx.fee != null && (
            <Typography fontSize={12}>
              <b>Commission :</b> {shownFee} {displayCurrency}
            </Typography>
          )}

          <Divider sx={{ borderStyle: "dashed", my: 0.6 }} />

          <Typography fontSize={12}>
            <b>Statut :</b> {statusLabel}
          </Typography>

          {!isMobile && tx.status === "partially_paid" && (
            <>
              <Typography fontSize={12}>
                <b>Déjà retiré :</b> {paidTotal.toFixed(2)} {tx.currency || ""}
              </Typography>
              <Typography fontSize={13} fontWeight="bold">
                <b>Reste à retirer :</b> {remainingAmount.toFixed(2)} {tx.currency || ""}
              </Typography>
            </>
          )}

          {!isMobile && <><Typography fontSize={12}>
            <b>Provenance :</b> {tx.sender_country} / {tx.sender_city}
          </Typography>

          <Typography fontSize={12}>
            <b>Destination :</b> {tx.receiver_country} / {tx.receiver_city}
          </Typography></>}
          {mobileRows.map(([label,value])=><Typography fontSize={12} key={label}><b>{label} :</b> {value}</Typography>)}

          <Typography fontSize={12}>
            <b>{dateLabel} :</b> {fmtDateFR(dateValue)}
          </Typography>

          {tx.delegate_name && tx.delegate_phone && (
            <>
              <Divider sx={{ borderStyle: "dashed", my: 0.6 }} />

              <Typography fontSize={12}>
                <b>Délégué :</b> {tx.delegate_name}
              </Typography>

              <Typography fontSize={12}>
                <b>Téléphone :</b> {tx.delegate_phone}
              </Typography>
            </>
          )}

          <Divider sx={{ borderStyle: "dashed", my: 0.6 }} />

          <Typography fontSize={10}>
            <b>Adresse :</b>
            <br />
            Chine: 广东省广州市越秀区越洋商贸城B区 3楼5317房号
          </Typography>

          <Typography fontSize={10}>
            Kinshasa : Croisement Av Itaga et Kasavubu,107 c/ Kinshasa,
            Galerie Grand Itaga, 1er niveau.
          </Typography>

          <Typography fontSize={10}>
            <b>Contact :</b> +86 13 242 307 259 / +243 85 339 22 04
          </Typography>

          <Divider sx={{ borderStyle: "dashed", my: 0.6 }} />

          <Typography textAlign="center" fontWeight="bold">
            Vous servir est notre devoir
          </Typography>
        </Paper>

        <Box display="flex" flexDirection="column" gap={1}>
          <Button variant="contained" size="small" onClick={printTopSectionOnly}>
            Imprimer
          </Button>

          <Button variant="outlined" size="small" onClick={downloadPdf}>
            Télécharger PDF
          </Button>

          <Button variant="outlined" size="small" onClick={shareReceipt}>
            Partager
          </Button>

          <Button variant="outlined" size="small" onClick={returnBack}>
            Retour
          </Button>
        </Box>
        <Snackbar
          open={notice.open}
          autoHideDuration={4500}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setNotice((prev) => ({ ...prev, open: false }))}
        >
          <Alert
            severity={notice.severity}
            onClose={() => setNotice((prev) => ({ ...prev, open: false }))}
          >
            {notice.message}
          </Alert>
        </Snackbar>
      </Box>
    </DashboardLayout>
  );
}
